/**
 * Arithmetic over dated rates.
 *
 * The whole point of the `Tarief` table is that a cost over an older month uses
 * the rate that applied *then*. That only works if the maths agrees, so two
 * decisions are fixed here:
 *
 * 1. **Everything normalises to a monthly amount.** A quarterly road-tax
 *    assessment, a yearly premium and a monthly instalment have to be
 *    comparable before they can be added.
 * 2. **A month is charged pro rata by days.** A rate that takes effect on the
 *    16th counts for the days it actually applied, not for the whole month and
 *    not for none of it. "Whichever rate held on the 1st" is barely simpler and
 *    loses or double-counts a change made mid-month, which is exactly the kind
 *    of quiet error this table exists to prevent.
 */
const MAANDEN_PER_PERIODE = { MAAND: 1, KWARTAAL: 3, JAAR: 12 };
/** The amount as a monthly figure, whatever it is billed per. */
export function perMaand(t) {
    return t.bedrag / (MAANDEN_PER_PERIODE[t.periode] ?? 1);
}
/** Whether `datum` falls inside this rate's interval (`geldigTot` exclusive). */
export function geldigOp(t, datum) {
    return datum >= t.geldigVan && (t.geldigTot == null || datum < t.geldigTot);
}
/** The rate of one soort that applied on `datum`, or null. */
export function tariefOpDatum(tarieven, soort, datum) {
    for (const t of tarieven)
        if (t.soort === soort && geldigOp(t, datum))
            return t;
    return null;
}
/** Every soort present in the list, in a stable order. */
export function tariefSoorten(tarieven) {
    return [...new Set(tarieven.map((t) => t.soort))].sort();
}
/**
 * What each soort costs in `YYYY-MM`, pro rata by the days each rate held.
 *
 * Soorten with no rate covering that month are simply absent, so a car that was
 * not yet insured in March does not get a March premium.
 */
export function vasteLastenInMaand(tarieven, maand) {
    const dagen = dagenInMaand(maand);
    if (!dagen)
        return {};
    const eersteDag = `${maand}-01`;
    const naDeMaand = volgendeMaandEersteDag(maand);
    const uit = {};
    for (const t of tarieven) {
        // Overlap of [geldigVan, geldigTot) with [eersteDag, naDeMaand), in days.
        const van = t.geldigVan > eersteDag ? t.geldigVan : eersteDag;
        const tot = t.geldigTot != null && t.geldigTot < naDeMaand ? t.geldigTot : naDeMaand;
        const overlap = dagVerschil(van, tot);
        if (overlap <= 0)
            continue;
        uit[t.soort] = (uit[t.soort] ?? 0) + perMaand(t) * (overlap / dagen);
    }
    return uit;
}
/**
 * The changes in one soort, oldest first: what it went from, to, and when.
 *
 * The first row on record is included with `vorige: null` and no delta — it is
 * where the history starts, not a change. Rows are ordered by `geldigVan`, so a
 * correction entered later still lands in its chronological place.
 */
export function tariefHistorie(tarieven, soort) {
    const reeks = tarieven
        .filter((t) => t.soort === soort)
        .slice()
        .sort((a, b) => (a.geldigVan < b.geldigVan ? -1 : a.geldigVan > b.geldigVan ? 1 : 0));
    return reeks.map((t, i) => {
        const vorige = i > 0 ? reeks[i - 1] : null;
        const oud = vorige ? perMaand(vorige) : null;
        const nieuw = perMaand(t);
        return {
            tarief: t,
            vorige,
            datum: t.geldigVan,
            deltaPerMaand: oud == null ? null : nieuw - oud,
            deltaDeel: oud == null || oud === 0 ? null : (nieuw - oud) / oud,
        };
    });
}
/**
 * Every change across all soorten, newest first — what the car's timeline shows.
 * Entries without a predecessor are included: "road tax recorded at € 150 per
 * quarter" is worth seeing even though it is not a change.
 */
export function alleTariefWijzigingen(tarieven) {
    return tariefSoorten(tarieven)
        .flatMap((s) => tariefHistorie(tarieven, s))
        .sort((a, b) => (a.datum < b.datum ? 1 : a.datum > b.datum ? -1 : 0));
}
/** Days in the month `YYYY-MM`, or 0 when the key is malformed. */
function dagenInMaand(maand) {
    const [j, m] = maand.split('-').map(Number);
    if (!j || !m || m < 1 || m > 12)
        return 0;
    return new Date(Date.UTC(j, m, 0)).getUTCDate();
}
/** First day of the month after `YYYY-MM`, as ISO `YYYY-MM-DD`. */
function volgendeMaandEersteDag(maand) {
    const [j, m] = maand.split('-').map(Number);
    const jaar = m === 12 ? j + 1 : j;
    const mnd = m === 12 ? 1 : m + 1;
    return `${jaar}-${String(mnd).padStart(2, '0')}-01`;
}
/** Whole days from ISO date `a` to ISO date `b`; negative when b precedes a. */
function dagVerschil(a, b) {
    const ms = Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`);
    return Number.isNaN(ms) ? 0 : Math.round(ms / 86_400_000);
}
//# sourceMappingURL=tarief.js.map